import { useEffect, useState } from "react";
import { ImageInfo } from "@/types/product";
import { useClosetStore } from "@/store";
import { handleSetProgressiveImageLoading } from "@/utils/progressiveImageLoading";

const loadingInitialState = {
  small_image: false,
  medium_image: false,
  large_image: false,
  original_image: false,
  empty_image: false
};

const useImageLoading = (image: ImageInfo, productPolingId?: string) => {
  const [imageURI, setImageURI] = useState<string | null>(null);
  const { updateClothingItemById } = useClosetStore((state) => state);

  const [loading, setLoading] = useState(loadingInitialState);
  const hasUnloadedImage = Object.values(loading).some((v) => v);

  useEffect(() => {
    // sometimes the image can be undefined, then we need to check if image changes and set new state
    setImageURI(image?.small_image || image?.medium_image || image?.large_image || image?.original_image);
  }, [image?.id, image?.original_image, image?.small_image, image?.medium_image, image?.large_image]);

  // If we provide a product id, we want to poll this product until it has a processed image
  // A processed image on a product is available on the derivatives (ie: small_image)
  let checkImageProcessingInterval: ReturnType<typeof setInterval>;

  const checkProductImageProcessing = async () => {
    await updateClothingItemById(productPolingId || "");
  };

  useEffect(() => {
    if (productPolingId && image?.original_image && !image?.small_image) {
      checkImageProcessingInterval = setInterval(checkProductImageProcessing, 30000);
    }

    return () => clearInterval(checkImageProcessingInterval);
  }, [image?.small_image]);

  const handleImageLoad = () => {
    if (image?.small_image === imageURI) {
      handleSetProgressiveImageLoading("small_image", setLoading);
      setImageURI(image?.medium_image);
    } else if (image?.medium_image === imageURI) {
      handleSetProgressiveImageLoading("medium_image", setLoading);
      setImageURI(image?.large_image);
    } else if (image?.large_image === imageURI) {
      handleSetProgressiveImageLoading("large_image", setLoading);
    } else if (image?.original_image === imageURI) {
      handleSetProgressiveImageLoading("original_image", setLoading);
      setImageURI(image?.original_image);
    } else {
      handleSetProgressiveImageLoading("empty_image", setLoading);
      setImageURI(null);
    }
  };

  return { imageURI, hasUnloadedImage, handleImageLoad, loading };
};

export { useImageLoading };
